<script>
    import { onMount } from "svelte";

    import {
        SearchSelect,
        SearchInput,
        PageLoading,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { dashboardFilterIntervals } from "../../../Utils/utilEnums.svelte";

    import {
        getDate,
        getTotalDaysInOffsetMonth,
        replaceURL,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import { isMobile, user } from "../../../stores";

    import DashboardContainer from "../../Dashboard/components/DashboardContainer.svelte";
    import TotalsItem from "../../Dashboard/components/TotalsItem.svelte";
    import EncountersItem from "../../Dashboard/components/EncountersItem.svelte";
    import TotalEncountersCreated from "../../Dashboard/components/TotalEncountersCreated.svelte";
    import TotalPaymentsChart from "../../Dashboard/components/TotalPaymentsChart.svelte";
    import MonthlyPaymentsItem from "../../Dashboard/components/MonthlyPaymentsItem.svelte";
    import TotalEncounterStatus from "../../Dashboard/components/TotalEncounterStatus.svelte";
  import SearchDateRangeInput from "../../../Components/Common/SearchDateRangeInput.svelte";

    export let accountId = "";
    export let params = {};

    let isGettingData = false,
        dashboardData = {},
        filters = { dateFrom: null, dateTo: null, interval: 2 };

    let totalEncountersCreated = {};

    let totalPatientBilled = {},
        totalHealthFundBilled = {};

    let totalPaymentsPatient = {},
        totalPaymentsHealthFund = {};

    let totalPatientPending = {},
        totalHealthFundPending = {};

    onMount(() => {
        if (params.query) syncInterval(params.query);

        handleIntervalSelect();
    });

    const handleIntervalSelect = () => {
        const today = new Date();
        const dateFrom = new Date();
        const dateTo = new Date();

        switch (filters.interval) {
            case 0:
                dateFrom.setMonth(today.getMonth() - 1);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 1:
                dateFrom.setMonth(today.getMonth() - 3);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 2:
                dateFrom.setMonth(today.getMonth() - 6);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 3:
                dateFrom.setFullYear(today.getFullYear() - 1);
                dateFrom.setMonth(0);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 5:
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 6:
                dateFrom.setMonth(0);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 4:
            default:
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
        }

        if (filters.dateFrom && filters.dateTo) {
            loadDashboardData(_getSearchParams());
            updateURL();
        }
    };

    const syncInterval = (query) => {
        const arr = query.split("&");

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].includes("interval="))
                filters.interval = Number(arr[i].split("=")[1]);

            if (arr[i].includes("dateFrom="))
                filters.dateFrom = arr[i].split("=")[1];

            if (arr[i].includes("dateTo="))
                filters.dateTo = arr[i].split("=")[1];
        }
    };

    const updateURL = () => {
        let url = `/Account/view/${params.id}/tab=accountInsight`;

        if (filters.interval || filters.interval == 0)
            url += `&interval=${filters.interval}`;

        if (filters.dateFrom) url += `&dateFrom=${filters.dateFrom}`;

        if (filters.dateTo) url += `&dateTo=${filters.dateTo}`;

        replaceURL(url);
    };

    const loadDashboardData = async (params) => {
        isGettingData = true;

        await apiMethods.getMethod
            .getDashboardDataForAdminAndManager(params, accountId)
            .then((res) => _handleDashboardData(res))
            .catch((err) => showException(err));

        isGettingData = false;
    };

    const handleDateSelect = () => {
        updateURL();

        if (filters.dateFrom && filters.dateTo) {
            loadDashboardData(_getSearchParams());
        }
    };

    // HELPER FUNCTIONS
    const _getSearchParams = () => {
        let params = {};

        if (filters.dateFrom) params["filter[startdate]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[enddate]"] = filters.dateTo;

        return params;
    };

    const _handleDashboardData = (res) => {
        if (res.error) showError(res.error);
        else {
            dashboardData = { ...res };

            if (res.encountersCreatedByDate)
                totalEncountersCreated = {
                    ...res.encountersCreatedByDate,
                };

            // Total Billed Data
            if (res.billedPatientsByDate)
                totalPatientBilled = {
                    ...res.billedPatientsByDate,
                };
            if (res.billedHealthFundByDate)
                totalHealthFundBilled = {
                    ...res.billedHealthFundByDate,
                };

            // Total Received Data
            if (res.paymentsReceivedPatientsByDate)
                totalPaymentsPatient = {
                    ...res.paymentsReceivedPatientsByDate,
                };
            if (res.paymentsReceivedHealthFundByDate)
                totalPaymentsHealthFund = {
                    ...res.paymentsReceivedHealthFundByDate,
                };

            // Total Pending Data
            if (res.pendingPaymentsPatientsByDate)
                totalPatientPending = { ...res.pendingPaymentsPatientsByDate };
            if (res.pendingPaymentsHealthFundByDate)
                totalHealthFundPending = {
                    ...res.pendingPaymentsHealthFundByDate,
                };
        }
    };
</script>

<div class="flex flex-col h-full m-4">
    <div class="flex justify-end items-center">
        <div class="flex gap-4">
            <SearchSelect
                bind:value={filters.interval}
                title="Interval"
                onChangeFunc={handleIntervalSelect}
                inputClass="bg-[#fff]"
                isDisabled={isGettingData}
            >
                {#each dashboardFilterIntervals as interval}
                    <option value={interval.id}>{interval.type}</option>
                {/each}
            </SearchSelect>

            {#if !$user.settings?.disableDateRangeInput}
                <SearchDateRangeInput
                    bind:startDate={filters.dateFrom}
                    bind:endDate={filters.dateTo}
                    title="Date Range"
                    inputClass="borderB bg-[#fff] w-[272px]"
                    on:change={handleDateSelect}
                    showClearButton
                    disabled={isGettingData || filters.interval != 4}
                />
            {:else}
                <SearchInput
                    bind:value={filters.dateFrom}
                    title="Date from"
                    type="date"
                    inputClass="bg-[#fff] borderB"
                    isLong
                    inputFunc={handleDateSelect}
                    isDisable={isGettingData || filters.interval != 4}
                    maxDate={filters.dateTo ?? undefined}
                />

                <SearchInput
                    bind:value={filters.dateTo}
                    title="Date to"
                    type="date"
                    inputClass="bg-[#fff] borderB"
                    isLong
                    inputFunc={handleDateSelect}
                    isDisable={isGettingData || filters.interval != 4}
                    minDate={filters.dateFrom ?? undefined}
                />
            {/if}
        </div>
    </div>

    <div class="flex flex-1 flex-col">
        <div class="flex flex-1 flex-col mb-4">
            <div class="relative flex-1">
                <div
                    class="flex flex-col overflow-auto absolute inset-0 gap-4 mt-4"
                >
                    <div
                        class="flex flex-1 gap-4 {$isMobile
                            ? 'flex-col'
                            : 'flex-row'}"
                    >
                        <DashboardContainer
                            title="Totals (Period / All Time)"
                            isLoadingData={isGettingData}
                        >
                            <TotalsItem totals={dashboardData.totals} accountId={accountId} />
                        </DashboardContainer>
                    </div>

                    <div
                        class="flex flex-1 gap-4 {$isMobile
                            ? 'flex-col'
                            : 'flex-row'}"
                    >
                        <DashboardContainer
                            title="Encounters"
                            isLoadingData={isGettingData}
                        >
                            <EncountersItem
                                encounters={dashboardData.totalEncoutnersByInsurance}
                            />
                        </DashboardContainer>

                        <DashboardContainer
                            title={"Encounter Statuses"}
                            isLoadingData={isGettingData}
                        >
                            <TotalEncounterStatus
                                statuses={dashboardData.totalEncoutnersByStatus}
                            />
                        </DashboardContainer>
                    </div>

                    <div class="flex flex-row flex-1">
                        <DashboardContainer
                            title="Total Encounters Created"
                            isLoadingData={isGettingData}
                        >
                            <TotalEncountersCreated
                                {totalEncountersCreated}
                                dateFrom={filters.dateFrom}
                                dateTo={filters.dateTo}
                            />
                        </DashboardContainer>
                    </div>

                    <div class="flex flex-row flex-1">
                        <DashboardContainer
                            title={"Billed"}
                            isLoadingData={isGettingData}
                        >
                            <TotalPaymentsChart
                                totalPaymentsPatient={totalPatientBilled}
                                totalPaymentsHealthFund={totalHealthFundBilled}
                                dateFrom={filters.dateFrom}
                                dateTo={filters.dateTo}
                                type="Billed"
                            />
                        </DashboardContainer>
                    </div>

                    <div class="flex flex-row flex-1">
                        <DashboardContainer
                            title={"Payments Received"}
                            isLoadingData={isGettingData}
                        >
                            <TotalPaymentsChart
                                {totalPaymentsPatient}
                                {totalPaymentsHealthFund}
                                dateFrom={filters.dateFrom}
                                dateTo={filters.dateTo}
                                type="Received"
                            />
                        </DashboardContainer>
                    </div>

                    <div class="flex flex-row flex-1">
                        <DashboardContainer
                            title={"Payments Pending"}
                            isLoadingData={isGettingData}
                        >
                            <TotalPaymentsChart
                                totalPaymentsPatient={totalPatientPending}
                                totalPaymentsHealthFund={totalHealthFundPending}
                                dateFrom={filters.dateFrom}
                                dateTo={filters.dateTo}
                                type="Pending"
                            />
                        </DashboardContainer>
                    </div>

                    <div class="flex flex-row flex-1">
                        <DashboardContainer
                            title="Monthly Payments"
                            isLoadingData={isGettingData}
                        >
                            <MonthlyPaymentsItem
                                monthlyPayments={dashboardData.monthlyPayments}
                            />
                        </DashboardContainer>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
